import React, {
  useState,
  useEffect,
  useContext,
  lazy,
  Suspense
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import axios from '../axiosAlertConfig';
import { UserContext } from '../../userAlertContext';

import styles from './DashboardCardHeader.module.css';
// import logo from './SchoolShield_color300x3xx.webp';
import logo from './logo192.png';

// Lazy-load the big dropdown so it won't download until needed
const LazyDropdownSearchInfrastructureMSG = lazy(() =>
  import('./DropdownSearchInfrastructure')
);


/**
 * DashboardCardHeader
 * 
 * @param {string} title - The header/title text
 * @param {function} onPrint - optional button handler
 * @param {function} onExportCSV - optional button handler
 * @param {function} onImportCSV - optional button handler
 * @param {function} onAddRow - optional button handler
 * @param {function} onSave - optional button handler
 * @param {function} onDeleteList - optional button handler
 * @param {function} onDelete - optional button handler
 * @param {string} closePath - optional path to navigate on close
 * @param {function} onCloseModal - optional callback for close
 * @param {boolean} hideDropdown - optional; if true, no dropdown is shown
 */
const DashboardCardHeader = ({
  title,
  onPrint,
  onExportCSV,
  onImportCSV,
  onAddRow,
  onSave,
  onDeleteList,
  onDelete,
  onLogout,
  closePath,
  onCloseModal,
  hideDropdown = false  // don't hide unless told otherwise
}) => {
  const navigate = useNavigate();
  const { user, iAccountID } = useContext(UserContext);

  // derived user display info for the name pill
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.userName ||
    user?.username ||
    '';

  const userInitials = fullName
    ? fullName
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0].toUpperCase())
        .slice(0, 2)
        .join('')
    : '??';

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // keep track of building infos
  const [buildingInfos, setBuildingInfos] = useState([]);
  const [infoLoaded, setInfoLoaded] = useState(false); // done fetching

  // fetch building infos in background
  useEffect(() => {
    const fetchData = async () => {
      try {
        // if missing IDs, skip
        if (!iAccountID || !user?.isAuthenticated) return;
        const response = await axios.get(`/buildinginfos?iAccountID=${iAccountID}`, {
          withCredentials: true,
        });
        setBuildingInfos(response.data);
      } catch (error) {
        console.error('Error fetching building infos:', error);
      } finally {
        setInfoLoaded(true);
      }
    };
    fetchData();
  }, [iAccountID, user?.isAuthenticated]);

  const handleClose = () => {
    if (onCloseModal) {
      onCloseModal();
      return;
    }
    if (closePath) {
      navigate(closePath);
      return;
    }
    // fallback navigation
    const fromPaths = [
      '/reportincidentpanel',
    ];
    if (fromPaths.includes(window.location.pathname)) {
      navigate('/reportincidentpanel');
    } else {
      navigate('/reportincidentpanel');
    }
  };

  return (
    <Container fluid className={styles.header}>
      <Row className="align-items-center">
        {/* LEFT: logo, title, optional dropdown */}
        <Col xs="auto" className={styles.leftSection}>
          <img src={logo} alt="Logo" className={styles.logo} />
          <h5 className={styles.title}>{title}</h5>

          {/* If data is still loading, show placeholder. If loaded, optionally display the dropdown */}
          {!infoLoaded ? (
            <span style={{ marginLeft: 10, fontStyle: 'italic' }}>
              Loading campus and building info...
            </span>
          ) : (
            // Only render the dropdown if hideDropdown is false
            !hideDropdown && (
              <Suspense fallback={<span>Loading dropdown...</span>}>
                <LazyDropdownSearchInfrastructureMSG
                  databaseContent={buildingInfos}
                  dropdownName="Campus/Building"
                />
              </Suspense>
            )
          )}
        </Col>

        {/* RIGHT: action buttons + user name pill + close icon */}
        <Col className="text-end">
          <div className={`d-none d-lg-inline-flex ${styles.centerActions}`}>
            {onPrint && (
              <button
                className={`btn btn-primary ${styles.actionButton}`}
                onClick={onPrint}
              >
                Print
              </button>
            )}
            {onExportCSV && (
              <button
                className={`btn btn-primary ${styles.actionButton}`}
                onClick={onExportCSV}
              >
                Export CSV
              </button>
            )}
            {onImportCSV && (
              <button
                className={`btn btn-primary ${styles.actionButton}`}
                onClick={onImportCSV}
              >
                Import CSV
              </button>
            )}
            {onDeleteList && (
              <button
                className={`btn btn-danger ${styles.actionButton}`}
                onClick={onDeleteList}
              >
                Delete List
              </button>
            )}
          </div>

       <div className={styles.rightActions}>
  {onSave && (
    <button
      className={`btn btn-primary ${styles.actionButton}`}
      onClick={onSave}
    >
      Save
    </button>
  )}
  {onAddRow && (
    <button
      className={`btn btn-primary ${styles.actionButton}`}
      onClick={onAddRow}
    >
      Add Row
    </button>
  )}
  {onDelete && (
    <button
      className={`btn btn-danger ${styles.actionButton}`}
      onClick={onDelete}
      disabled
    >
      Delete
    </button>
  )}

  {onLogout && (
    <button
      type="button"
      className={`btn btn-outline-warning btn-sm ${styles.actionButton}`}
      onClick={onLogout}
      aria-label="Logout"
      title="Logout"
    >
      Logout
    </button>
  )}

  {/* Name pill / user menu */}
  {fullName && (
              <div className={styles.userMenuWrapper}>
                <button
                  type="button"
                  className={`${styles.userMenuToggle} ${
                    userMenuOpen ? styles.userMenuToggleOpen : ''
                  }`}
                  onClick={() => setUserMenuOpen((open) => !open)}
                >
                  {/* <span
                    className={styles.userMenuIcon}
                    aria-hidden="true"
                  >
                    👤
                  </span> */}


<span className={styles.userMenuIcon} aria-hidden="true">
  <i className="bi bi-person-fill" />
</span>




                  <span className={styles.userMenuText}>
                    Welcome,&nbsp;{userInitials}
                  </span>






                  <span
                    className={styles.userMenuChevron}
                    aria-hidden="true"
                  >
                    {userMenuOpen ? '▴' : '▾'}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className={styles.userMenuDropdown}>
                    <div className={styles.userMenuName}>
                      {fullName}
                    </div>
                    <div className={styles.userMenuId}>
                      Account: {user?.accountID ?? iAccountID}{' '}
                      &nbsp;·&nbsp; ID:&nbsp;{user?.userID}
                    </div>
                    <div className={styles.userMenuRole}>
                      {user?.userRole}
                    </div>
                  </div>
                )}
              </div>
            )}

            <span className={styles.closeButton} onClick={handleClose}>
              &times;
            </span>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default DashboardCardHeader;



